import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { downloadCSV } from '../../utils/csv';
import { getTodayDateStringInBusinessTZ } from '../../utils/date';
import { BRAND_KEYS, DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { REVENUE_SHARE } from '../../config/revenueShare';
import type { RevenueShareStatementRow } from '../../types/revenueShare';
import {
  buildStatementModel,
  centsToDollars,
  defaultWorkbenchState,
  monthLabel,
  otherBrandCollections,
  parseWorkbenchState,
  rowsForBrand,
  serializeWorkbenchState,
  stepMonth,
  useStatementModel,
  type WorkbenchState,
} from '../utils/useStatementModel';
import { buildStatementCsv } from '../utils/statementCsv';
import { CommandBar } from '../components/facility-rent/CommandBar';
import { KpiStrip, type MonthBooking } from '../components/facility-rent/KpiStrip';
import { CompositionBar } from '../components/facility-rent/CompositionBar';
import { RulesRail } from '../components/facility-rent/RulesRail';
import { StatementTable } from '../components/facility-rent/StatementTable';
import { StatementPrintPreview } from '../components/facility-rent/StatementPrintPreview';

function useStatementRows(month: string, enabled = true) {
  return useQuery({
    queryKey: ['revenue-share-statement', month],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RevenueShareStatementRow[]> => {
      const { data, error } = await supabase.rpc('revenue_share_statement', {
        p_month: `${month}-01`,
      });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * Invoiced-for-the-month total, for the Gross booked KPI tile. A separate call from the
 * statement rows on purpose: those are payment allocations, so an invoice nobody paid
 * produces no row and is invisible to them. Failure is non-fatal — the tile shows a dash
 * and the rent statement, which does not depend on this, renders as normal.
 */
function useMonthBooking(month: string) {
  return useQuery({
    queryKey: ['month-gross-booking', month],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MonthBooking> => {
      const { data, error } = await supabase.rpc('month_gross_booking', {
        p_month: `${month}-01`,
      });
      if (error) throw new Error(error.message);
      return data as unknown as MonthBooking;
    },
  });
}

/**
 * Facility Rent Statement Workbench.
 *
 * Facts from the server (one fetch of payment-level allocation rows per month);
 * composition on the client (rules, overrides, exclusions, adjustments — all
 * pure arithmetic, all serialized into the URL). Nothing is persisted: the
 * CSV/PDF export is the frozen paid-record.
 */
export default function AdminFacilityRent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMonth = getTodayDateStringInBusinessTZ().slice(0, 7);
  const month = /^\d{4}-\d{2}$/.test(searchParams.get('month') ?? '')
    ? (searchParams.get('month') as string)
    : currentMonth;
  const state = useMemo(() => parseWorkbenchState(searchParams.get('s')), [searchParams]);
  /**
   * Whose statement this is. One brand at a time, with no "all brands" option on purpose: the
   * rent is owed per site under that brand's own agreement, so a merged statement is a number
   * nobody owes. Kaleido revenue must never raise the BHCS rent, and the reverse.
   */
  const brandParam = searchParams.get('brand');
  const brand: BrandKey = isBrandKey(brandParam) ? brandParam : DEFAULT_BRAND_KEY;

  const updateUrl = useCallback(
    (nextMonth: string, nextState: WorkbenchState, nextBrand: BrandKey = brand) => {
      const params = new URLSearchParams();
      if (nextMonth !== currentMonth) params.set('month', nextMonth);
      if (nextBrand !== DEFAULT_BRAND_KEY) params.set('brand', nextBrand);
      const s = serializeWorkbenchState(nextState);
      if (s) params.set('s', s);
      setSearchParams(params, { replace: true });
    },
    [brand, currentMonth, setSearchParams],
  );

  const onChange = useCallback(
    (patch: Partial<WorkbenchState>) => updateUrl(month, { ...state, ...patch }),
    [month, state, updateUrl],
  );

  // The RPC returns every brand's rows, tagged; the statement is built from this brand's only.
  const { data: allBrandRows = [], isLoading, error, refetch } = useStatementRows(month);
  const rows = useMemo(() => rowsForBrand(allBrandRows, brand), [allBrandRows, brand]);
  const otherBrands = useMemo(() => otherBrandCollections(allBrandRows, brand), [allBrandRows, brand]);
  // Prior month at default rules, for the small delta on the Rent Owed tile.
  const { data: priorRows } = useStatementRows(stepMonth(month, -1));
  const { data: booking } = useMonthBooking(month);

  const model = useStatementModel(rows, state);
  const priorRentCents = useMemo(
    () =>
      priorRows === undefined
        ? undefined
        : buildStatementModel(rowsForBrand(priorRows, brand), { ...defaultWorkbenchState(), sharePct: state.sharePct })
            .totals.rentCents,
    [priorRows, brand, state.sharePct],
  );
  const profile = BRAND_PROFILES[brand];

  const dirty = serializeWorkbenchState(state) !== '';
  const [printPreview, setPrintPreview] = useState(false);
  // Narrow layouts only (the toggle is hidden on lg+, where the rail is always shown).
  const [rulesOpen, setRulesOpen] = useState(false);

  function exportCsv() {
    // The default brand keeps the file name it always had.
    const who = brand === DEFAULT_BRAND_KEY ? '' : `${brand}-`;
    downloadCSV(buildStatementCsv(model, state, month, new Date(), brand), `revenue-share-${who}${month}.csv`);
  }

  function copyLink() {
    void navigator.clipboard.writeText(window.location.href);
  }

  // Print preview replaces the whole layout tree (same pattern as
  // WaiverPrintPreview) so the browser prints a clean statement page.
  if (printPreview) {
    return (
      <StatementPrintPreview model={model} state={state} month={month} brand={brand} onBack={() => setPrintPreview(false)} />
    );
  }

  return (
    <AdminLayout>
      {/* AdminLayout is h-screen overflow-hidden in desktop mode (>=768px) — the page provides
          its own scroll container. md, not lg: it must scroll at every desktop width, including
          the 768-1024px band where the workbench grid stacks into one column. */}
      <div className="md:flex-1 md:min-h-0 md:overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
        <div>
          <Link
            to="/admin-app/finances"
            className="text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600"
          >
            Admin · Finances
          </Link>
          <h1 className="text-xl font-bold text-gray-900">Revenue Share Statement</h1>
        </div>

        {/* Which brand's statement. Above everything else because it changes what every figure
            below means. Switching resets the workbench: overrides, exclusions and adjustments
            were made for one brand's statement and mean nothing on another's. */}
        <div className="space-y-2 print:hidden">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Statement for:</span>
            <div className="inline-flex rounded-lg border border-gray-300 p-0.5">
              {BRAND_KEYS.map((key) => {
                const p = BRAND_PROFILES[key];
                const isActive = key === brand;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => {
                      if (!isActive) updateUrl(month, defaultWorkbenchState(), key);
                    }}
                    className="flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                    style={isActive ? { backgroundColor: p.accent, color: '#fff' } : { color: '#4b5563' }}
                  >
                    {p.shortName}
                  </button>
                );
              })}
            </div>
          </div>
          {otherBrands.length > 0 && (
            <p className="text-xs text-gray-500" data-testid="other-brand-collections">
              Not in this statement:{' '}
              {otherBrands.map((o, i) => (
                <span key={o.brand}>
                  {i > 0 && '; '}
                  {BRAND_PROFILES[o.brand].shortName} collected {o.netCents < 0 ? '−' : ''}$
                  {centsToDollars(Math.abs(o.netCents))} in {monthLabel(month)} ({o.nPayments} payment
                  {o.nPayments === 1 ? '' : 's'}
                  {o.nRefunds > 0 ? `, ${o.nRefunds} refund${o.nRefunds === 1 ? '' : 's'}` : ''})
                </span>
              ))}
              .
            </p>
          )}
          {brand !== DEFAULT_BRAND_KEY && (
            <div role="note" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              No revenue-share terms are on file for {profile.shortName}. The share starts at the{' '}
              {BRAND_PROFILES[DEFAULT_BRAND_KEY].shortName} default of {Math.round(REVENUE_SHARE.defaultPct * 100)}%. Set{' '}
              {profile.shortName}&apos;s own before you export or print.
            </div>
          )}
        </div>

        <CommandBar
          month={month}
          sharePct={state.sharePct}
          dirty={dirty}
          onMonthStep={(delta) => updateUrl(stepMonth(month, delta), state)}
          onSharePct={(sharePct) => onChange({ sharePct })}
          onReset={() => updateUrl(month, defaultWorkbenchState())}
          onExportCsv={exportCsv}
          onPrint={() => setPrintPreview(true)}
          onCopyLink={copyLink}
        />

        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-500">Computing {monthLabel(month)} statement…</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Failed to load the statement: {error instanceof Error ? error.message : 'unknown error'}{' '}
            <button type="button" onClick={() => void refetch()} className="font-medium underline">
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">
            No {profile.shortName} tuition was collected in {monthLabel(month)}.
          </div>
        ) : (
          <>
            <KpiStrip totals={model.totals} priorRentCents={priorRentCents} booking={booking} brand={brand} />
            <CompositionBar model={model} state={state} month={month} />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[16rem_1fr]">
              <aside className="print:hidden">
                {/* Stacked (narrow) layout: rules collapse by default; always open beside the table on lg+. */}
                <button
                  type="button"
                  onClick={() => setRulesOpen((v) => !v)}
                  aria-expanded={rulesOpen}
                  className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-900 lg:hidden"
                >
                  Statement rules
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${rulesOpen ? 'rotate-180' : ''}`} />
                </button>
                <h2 className="mb-2 hidden text-sm font-semibold text-gray-900 lg:block">Statement rules</h2>
                <div className={`${rulesOpen ? 'mt-3 block' : 'hidden'} lg:mt-0 lg:block`}>
                  <RulesRail state={state} model={model} onChange={onChange} />
                </div>
              </aside>
              <section className="min-w-0 overflow-x-auto rounded-xl border border-gray-200 bg-white p-3">
                <StatementTable model={model} state={state} onChange={onChange} />
              </section>
            </div>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}
